import { useScrollPostionRestoration } from '@/utils/dom'
import { css } from '@emotion/react'
import { useRouter } from '@tanstack/react-router'
import { HTMLMotionProps, motion, useMotionValueEvent, useScroll } from 'motion/react'
import { type ReactElement, type ReactNode, forwardRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import Button from './Button'

export interface PageProps extends Omit<HTMLMotionProps<'div'>, 'title' | 'children'> {
	children?: ReactNode
	title?: ReactNode

	/**
	 * 是否显示标题栏
	 *
	 * @default !!title
	 */
	isHeaderShow?: boolean
	/**
	 * 沉浸式标题栏
	 *
	 * @default false
	 */
	isImmersiveHeader?: boolean
	onReachPageBottom?: () => void
	right?: ReactElement
}

export default forwardRef<HTMLDivElement, PageProps>(function Page(
	{
		title,
		children,
		onReachPageBottom,
		right,
		isHeaderShow = !!title,
		isImmersiveHeader = false,
		...props
	}: PageProps,
	ref
) {
	const { scrollY } = useScroll()
	const [isScrolled, setIsScrolled] = useState<boolean>(false)

	const { ref: loadMoreRef } = useInView({
		onChange: (inView) => {
			if (inView) {
				onReachPageBottom?.()
			}
		},
	})
	const router = useRouter()
	const isScrollPositionRestored = useScrollPostionRestoration()
	const isUnscrolledImmersiveHeader = isImmersiveHeader && !isScrolled

	useMotionValueEvent(scrollY, 'change', (val) => {
		setIsScrolled(val > 30)
	})

	return (
		<motion.div
			initial={{
				opacity: 0,
			}}
			animate={
				isScrollPositionRestored && {
					opacity: 1,
					transition: {
						duration: 0.1,
					},
				}
			}
			ref={ref}
			css={css`
				display: flex;
				flex-direction: column;
				align-items: center;
			`}
			{...props}
		>
			{isHeaderShow && (
				<header
					css={css`
						position: fixed;
						top: 0;
						left: 50vw;
						z-index: 2;
						transform: translateX(-50%);
						width: 100%;
						max-width: var(--page-container-width);
						font-size: 20px;
						font-weight: 500;
						line-height: 28px;
						letter-spacing: 0.25px;
						padding: 16px 15px;
						text-align: center;
						display: flex;
						align-items: center;
						background-color: var(--bg-body-1);
						box-shadow: 0 0.5px 0 0 var(--tint-separator);
						transition:
							background-color 0.3s,
							box-shadow 0.3s;

						> * {
							flex: 1;
						}

						${isUnscrolledImmersiveHeader &&
						css`
							background-color: rgba(0, 0, 0, 0);
							box-shadow: 0 0.5px 0 0 rgb(0 0 0 / 0%);
						`}
					`}
				>
					<div>
						<Button
							css={css`
								padding: 4px;
								min-width: unset;
								aspect-ratio: 1;
							`}
							appearance={isUnscrolledImmersiveHeader ? 'glass' : 'ghost'}
							size="sm"
							onClick={() => {
								router.history.back()
							}}
						>
							&#8592;
						</Button>
					</div>
					<div
						css={css`
							white-space: nowrap;
						`}
					>
						{title}
					</div>
					<div>{right}</div>
				</header>
			)}

			<div
				css={css`
					width: 100%;
					flex: 1;
					display: flex;
					justify-content: center;
					${isHeaderShow &&
					!isImmersiveHeader &&
					css`
						margin-top: 62px;
					`}
				`}
			>
				<div
					css={css`
						width: 100%;
						max-width: var(--page-container-width);
					`}
				>
					{children}

					{onReachPageBottom && (
						<div
							ref={loadMoreRef}
							css={css`
								height: 100px;
							`}
						/>
					)}
				</div>
			</div>
		</motion.div>
	)
})
